/**
 * Typed, render-time report prose values.
 *
 * Executive-summary copy persists references rather than interpolated values,
 * so an assumption edit changes displayed figures without rewriting prose.
 */

import type {
  ReportPricingShape,
  ReportProseAssertion,
  ReportProseSegment,
  ReportValueRef,
} from "@atlas/computation";
import { formatPricingShapeSentence } from "@atlas/computation";
import {
  formatHorizonYears,
  formatReportRoiPercent,
  formatReportUsd,
  REPORT_EMPTY_VALUE,
} from "./report-format";
import type {
  ReportMetricRow,
  ReportProseData,
  ReportTraceNode,
} from "./report-types";

export type {
  ReportProseAssertion,
  ReportProseSegment,
  ReportValueRef,
} from "@atlas/computation";

export type ResolvedProseSegment =
  | { kind: "text"; text: string }
  | {
      kind: "ref";
      ref: ReportValueRef;
      text: string;
      /** A verified key into `ReportData.traceNodes`, when one exists. */
      traceNodeId?: string;
    };

interface ResolvedReference {
  text: string;
  traceNodeId?: string;
}

export interface ReportProseResolutionContext {
  /** Live weakness ranking; omitted only when the caller has not loaded it. */
  weakness?: {
    rankedInputs: Array<{ id: string; name: string }>;
    topInput: { id: string; name: string } | null;
  };
  /** `undefined` means not loaded; `null` means the live model has no mix. */
  evidenceMix?: string | null;
  /** Omitted while the live assumptions view is unavailable. */
  uncitedInputCount?: number;
  /**
   * Cost-stream structure, known even before the projection computes.
   * `undefined` means not loaded; `"none"` means the model has no cost side,
   * which resolves `missing` rather than asserting an absent upfront fee.
   */
  pricingShape?: ReportPricingShape;
  /** One predicate, shared by resolution and assertion validation. */
  isUnsafeRef?: (ref: ReportValueRef) => boolean;
}

export interface UnresolvedReportValueRef {
  ref: ReportValueRef;
  reason: "pending" | "missing" | "unsafe";
}

type ReferenceResolution =
  | { status: "resolved"; value: ResolvedReference }
  | { status: "unresolved"; reason: UnresolvedReportValueRef["reason"] };

function traceableNodeId(
  data: ReportProseData,
  id: string,
  expectedKind: ReportTraceNode["kind"],
): string | undefined {
  const node = data.traceNodes[id];
  return node?.kind === expectedKind && !node.ambiguous ? id : undefined;
}

function isDisplayValue(value: string | null | undefined): value is string {
  return value != null && value !== REPORT_EMPTY_VALUE;
}

function assumptionNode(
  data: ReportProseData,
  id: string,
): ReportTraceNode | undefined {
  const nodeKey = data.leafNodeKeys[`assumption:${id}`];
  if (!nodeKey) return undefined;
  const node = data.traceNodes[nodeKey];
  return node?.kind === "assumption" && !node.ambiguous ? node : undefined;
}

function metricById(
  data: ReportProseData,
  id: string,
): ReportMetricRow | undefined {
  return data.metrics.find((metric) => metric.id === id);
}

function topBenefit(data: ReportProseData): ReportMetricRow | undefined {
  return [...data.metrics]
    .filter((metric) => metric.amountValue != null)
    .sort(
      (left, right) =>
        (right.amountValue ?? 0) - (left.amountValue ?? 0) ||
        left.id.localeCompare(right.id),
    )[0];
}

function resolved(value: ResolvedReference): ReferenceResolution {
  return { status: "resolved", value };
}

function unresolved(
  reason: UnresolvedReportValueRef["reason"],
): ReferenceResolution {
  return { status: "unresolved", reason };
}

function resolveReference(
  ref: ReportValueRef,
  data: ReportProseData,
  context?: ReportProseResolutionContext,
): ReferenceResolution {
  if (context?.isUnsafeRef?.(ref)) return unresolved("unsafe");

  switch (ref.kind) {
    case "figure": {
      if (!data.figures.ready) return unresolved("pending");

      if (ref.key === "totalCost" || ref.key === "netSavings") {
        const value = data.figures[ref.key];
        if (value == null) return unresolved("pending");
        return resolved({
          text: formatReportUsd(value),
          traceNodeId: traceableNodeId(data, ref.key, "figure"),
        });
      }
      if (ref.key === "roi") {
        if (data.figures.roiPercent == null) return unresolved("pending");
        return resolved({
          text: formatReportRoiPercent(data.figures.roiPercent),
          traceNodeId: traceableNodeId(data, ref.key, "figure"),
        });
      }
      if (!data.figures.paybackLabel) return unresolved("pending");
      return resolved({
        text: data.figures.paybackLabel,
        traceNodeId: traceableNodeId(data, ref.key, "figure"),
      });
    }
    case "horizon": {
      if (!data.figures.ready) return unresolved("pending");
      const text = formatHorizonYears(data.figures.horizonYears);
      return text ? resolved({ text }) : unresolved("pending");
    }
    case "metric": {
      const metric = metricById(data, ref.id);
      if (!metric) return unresolved("missing");
      const text = ref.field === "label" ? metric.label : metric.amount;
      if (!isDisplayValue(text)) return unresolved("pending");
      return resolved({
        text,
        traceNodeId: traceableNodeId(data, metric.traceNodeId, "metric"),
      });
    }
    case "assumption": {
      const node = assumptionNode(data, ref.id);
      if (!node) return unresolved("missing");
      const text = ref.field === "name" ? node.label : node.value;
      if (!isDisplayValue(text)) return unresolved("pending");
      return resolved({
        text,
        traceNodeId: node.id,
      });
    }
    case "pricingShape": {
      if (!context?.pricingShape) return unresolved("pending");
      const text = formatPricingShapeSentence(context.pricingShape);
      // No cost stream and no cost meta: there is no pricing to describe, and
      // "Subscription only. No upfront fee." would state one from the absence
      // of cost data. Drop the bullet instead.
      if (text === null) return unresolved("missing");
      return resolved({ text });
    }
    case "topBenefit": {
      const metric = topBenefit(data);
      if (!metric) {
        return data.metrics.length === 0
          ? unresolved("missing")
          : unresolved("pending");
      }
      const text = ref.field === "label" ? metric.label : metric.amount;
      if (!isDisplayValue(text)) return unresolved("pending");
      return resolved({
        text,
        traceNodeId: traceableNodeId(data, metric.traceNodeId, "metric"),
      });
    }
    case "softestInput": {
      if (!context?.weakness) return unresolved("pending");
      if (!context.weakness.topInput) return unresolved("missing");
      const node = assumptionNode(data, context.weakness.topInput.id);
      if (!node) return unresolved("missing");
      return resolved({ text: node.label, traceNodeId: node.id });
    }
    case "evidenceMix": {
      if (context?.evidenceMix === undefined) return unresolved("pending");
      return context.evidenceMix
        ? resolved({ text: context.evidenceMix })
        : unresolved("missing");
    }
    case "uncitedInputCount": {
      return context?.uncitedInputCount == null
        ? unresolved("pending")
        : resolved({ text: String(context.uncitedInputCount) });
    }
    case "assumptionCount": {
      return context?.weakness
        ? resolved({ text: String(context.weakness.rankedInputs.length) })
        : unresolved("pending");
    }
  }
}

/** Resolves live report values, preserving fallback copy for absent values. */
export function resolveReportProse(
  segments: ReportProseSegment[],
  data: ReportProseData,
  context?: ReportProseResolutionContext,
): {
  segments: ResolvedProseSegment[];
  unresolved: UnresolvedReportValueRef[];
} {
  const unresolvedRefs: UnresolvedReportValueRef[] = [];
  const resolved = segments.map((segment): ResolvedProseSegment => {
    if (segment.kind === "text") return segment;
    const resolution = resolveReference(segment.ref, data, context);
    if (resolution.status === "resolved") {
      return { kind: "ref", ref: segment.ref, ...resolution.value };
    }
    unresolvedRefs.push({ ref: segment.ref, reason: resolution.reason });
    return { kind: "ref", ref: segment.ref, text: segment.pending };
  });

  return { segments: resolved, unresolved: unresolvedRefs };
}

function numericReference(
  ref: ReportValueRef,
  data: ReportProseData,
  context?: ReportProseResolutionContext,
): number | undefined {
  if (context?.isUnsafeRef?.(ref)) return undefined;
  switch (ref.kind) {
    case "figure":
      if (!data.figures.ready) return undefined;
      if (ref.key === "roi") return data.figures.roiPercent ?? undefined;
      // `paybackLabel` is formatted display text, not a numeric source.
      if (ref.key === "payback") return undefined;
      return data.figures[ref.key] ?? undefined;
    case "horizon":
      return data.figures.ready
        ? (data.figures.horizonYears ?? undefined)
        : undefined;
    case "metric": {
      // Only the amount is a number. A `within` assertion pinned to a metric's
      // *label* has no numeric reading, and answering one with the amount lets
      // a malformed assertion pass a bound it never meant — keeping a stale
      // qualitative bullet that the check exists to drop. `topBenefit` below
      // makes the same distinction.
      if (ref.field !== "amount") return undefined;
      const metric = metricById(data, ref.id);
      return metric?.amountValue ?? undefined;
    }
    case "assumption":
      // Assumption trace values are formatted strings for the same reason.
      return undefined;
    case "pricingShape":
      return undefined;
    case "topBenefit":
      return ref.field === "amount"
        ? (topBenefit(data)?.amountValue ?? undefined)
        : undefined;
    case "softestInput":
    case "evidenceMix":
    case "uncitedInputCount":
    case "assumptionCount":
      return undefined;
  }
}

function metricAmount(metric: ReportMetricRow): number | undefined {
  return metric.amountValue ?? undefined;
}

/**
 * Checks whether qualitative statements are still supported by live data.
 * A false return tells the caller to omit the entire bullet rather than render
 * a stale judgement beside freshly resolved figures.
 */
export function validateProseAssertions(
  assertions: ReportProseAssertion[],
  data: ReportProseData,
  context?: ReportProseResolutionContext,
): boolean {
  return assertions.every((assertion) => {
    if (assertion.kind === "within") {
      const value = numericReference(assertion.ref, data, context);
      return value != null && value >= assertion.low && value <= assertion.high;
    }

    if (assertion.kind === "ordering") {
      const amounts = assertion.metricIds.map((id) => {
        const metric = metricById(data, id);
        return metric ? metricAmount(metric) : undefined;
      });
      return (
        amounts.every((amount) => amount != null) &&
        amounts.every(
          (amount, index) =>
            index === 0 || (amounts[index - 1] ?? 0) >= (amount ?? 0),
        )
      );
    }

    const ranked = data.metrics
      .map((metric) => ({ id: metric.id, amount: metricAmount(metric) }))
      .filter(
        (metric): metric is { id: string; amount: number } =>
          metric.amount != null,
      )
      .sort(
        (left, right) =>
          right.amount - left.amount || left.id.localeCompare(right.id),
      );
    return ranked[0]?.id === assertion.metricId;
  });
}
